import { defineStore } from 'pinia';
import apiService from '@/services/api.service';
import { Config } from '@/config';
import { getBrandingPresets as resolveBrandingPresets } from '@/helpers/branding-preset.helper';
import type {
  BrandingPresetConfigurationItem,
  ConfigurationDto,
  ProjectModule,
  ProjectThemesRowItem,
  ThemeHost,
  ThemeManifestEntry,
} from '@/services/types/api.types';
import type { ModuleTypeEnum } from '@/types/modules';

const enabledModules = (configurations: ConfigurationDto[]): ProjectModule[] =>
  configurations.find((c) => c.key === 'project.modules')?.value?.filter((m: ProjectModule) => m.enabled === true);

// Until COCO-3275 removes the `project.themes` row, an API that does not serve `themes` yet -- the admin deployed
// before it -- falls back to the row, read as the admin read it before the manifest: its variants and cropping, and
// every enabled block type offered on projects. Pages have no row to fall back on, so no page theme is listed then.
const themesFromProjectThemesRow = (configurations: ConfigurationDto[]): ThemeManifestEntry[] => {
  const row = (configurations.find((c) => c.key === 'project.themes')?.value ?? []) as ProjectThemesRowItem[];
  const enabled = enabledModules(configurations) ?? [];
  return row.map((theme) => ({
    key: theme.key,
    label: theme.label,
    parent: null,
    hosts: {
      project: {
        offers: Object.fromEntries(
          enabled.map((module) => [
            module.value,
            { variants: theme.modules?.[module.value as ModuleTypeEnum]?.layouts ?? [] },
          ]),
        ),
      },
    },
  }));
};

export interface ConfigurationState {
  configurations: ConfigurationDto[];
}

export const useConfigurationStore = defineStore({
  id: 'configuration',
  state: (): ConfigurationState => ({
    configurations: [],
  }),
  getters: {
    isFeatureEnabled:
      (state) =>
      (key: string): boolean => {
        const features = state.configurations.find((c) => c.key === 'feature.release-flags')?.value;
        if (!features || !(key in features)) {
          return false;
        }
        return features[key];
      },
    getProjectModulesEnabled: (state) => (): ProjectModule[] => enabledModules(state.configurations),
    // The themes that cover a host, in the manifest's order.
    getThemes:
      (state) =>
      (host: ThemeHost): ThemeManifestEntry[] => {
        const served = state.configurations.find((c) => c.key === 'themes')?.value as ThemeManifestEntry[] | undefined;
        const manifest = served ?? themesFromProjectThemesRow(state.configurations);
        return manifest.filter((theme) => theme.hosts[host]);
      },
    getBrandingPresets: (state) => (): BrandingPresetConfigurationItem[] => {
      const presets = state.configurations.find((c) => c.key === 'branding.presets')?.value;
      return resolveBrandingPresets(presets);
    },
  },
  actions: {
    async init() {
      console.debug('init configurations store');
      await this.fetchConfigurations();
    },
    async fetchConfigurations() {
      try {
        const { data } = await apiService.get<ConfigurationDto[]>(`${Config.api.v2}/configurations`);
        this.configurations = data;
      } catch (error) {
        console.error(error);
      }
    },
  },
});
